<template>
  <main id="applicator-map-view">

    <div class="map-placeholder" v-if="!hasSearchQuery"  :class="!searchQuery ? 'landing' : ''">
      <div class="bg-image" :style="!hasSearchQuery ? 'opacity: .4;' : 'opacity: 1'"></div>
      <div class="bg-text" v-if="visible">
        <h1>Find a certified Cerakote applicator near You</h1>
        <button class="btn btn-blue" type="button" @click="this.GetUsersGeoLocation">
          <i class="fa fa-map-marker"></i> Use my location
        </button>
        <form id="applicator-map-search" class='initial' @submit.prevent="LocationSearch">
          <p class='initial'><i class="fa fa-minus fa-1" style='font-size: smaller;' aria-hidden="true"></i> OR <i class="fa fa-minus fa-1"  style='font-size: smaller;' aria-hidden="true"></i></p>
          <div id="applicator-map-search-center">
            <div id="applicator-map-search-input">
              <input type="search" placeholder="Enter your city, state or postal code" v-model="searchQuery" />
              <button type="submit" :disabled="!searchQuery">
                <i class="fa fa-search"></i>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div id="applicator-map-controls">
      <div id="applicator-map-controls-right">
        <form v-if="instance && ready" id="applicator-map-zoom" class="map-action">
          <button type="button" id="applicator-map-zoom-in" @click.prevent="SetMapZoom(MapZoom + 1)">
            <i class="fa fa-plus"></i>
          </button>
          <button type="button" id="applicator-map-zoom-out" @click.prevent="SetMapZoom(MapZoom - 1)">
            <i class="fa fa-minus"></i>
          </button>
        </form>
      </div>
    </div>

    <div id="applicator-map-view-init"></div>

    <div v-if="instance && ready" id="applicator-map-markers">
      <applicator-map-marker v-for="applicator in applicators" ref="markers" :applicator="applicator" :key="applicator.user_id" @click="MapMarkerClicked" @close-applicator="DeselectMarkers" />
    </div>

  </main>
</template>

<script>
  export default {
    name: "applicator-map",
    props: { applicators: Array },

    data() {
      return {

        events: [],

        searchQuery: null,
        urlParams: {},
        filters: {},

        ready: false,
        instance: undefined,
        selector: "applicator-map-view-init",
        lat: null,
        lng: null,
        zoom: 10,
        radius: 40,
        oldRadius: 40,
        radian: 1609.34,
        radiusCircle: undefined,
        hasSearchQuery: false,
        visible: false,

        notifications: [],

        geocoder: new google.maps.Geocoder(),

        options: {

          // When set to true, a visible radius is drawn onto the map
          // illustrating the current search area.
          showRadius: true,

          // The maximum number of map markers allowed to be displayed
          // on the map at one time.
          maximumMarkers: 100,

          // The maximum search radius allowed. This helps keep API responses
          // down to a reasonable size.
          maximumRadius: 800,

          // When set to true, dragging the map will trigger a new
          // API request to fetch new applicators for the visible area
          // once the drag event has completed and the map returns to
          // an idle state.
          updateOnDrag: true,

          updateOnZoom: true,

          clickableIcons: false,

          // When set to true, clicking a map marker will pan the map
          // to center on the selected icon.
          centerOnSelectedMarker: true,

          disableDefaultUI: true,

          gestureHandling: "cooperative",

          // When set to true, displays the default Google map controls for displaying
          // the street view functionality. Setting to false hides the street view controls.
          streetViewControl: false,

          navigationControl: false,

          // When set to true, displays the default Google map controls for switching
          // between different map types (terrain, satellite, etc.). Setting to false
          // hides the map type controls.
          mapTypeControl: false,

          scaleControl: false,

          // When set to true, displays the default Google map interface for controlling
          // the zoom level of the map. When set to false, it hides the default zoom controls
          // and instead shows the custom map zoom controls.
          zoomControl: false,

          // When set to true, enables the default scrollwheel events to trigger
          // on the map. When set to false, all scrollwheel events are ignored on
          // the map, allowing the page to be scrolled even when moused over the map.
          scrollwheel: false

        }

      }
    },

    computed: {


      /**
       * @method MapBounds
       * @created September 4th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Gets the physical bounds of the map.
       * @returns Object
       */

      MapBounds() {

        if (this.instance) {
          return this.instance.getBounds();
        }

      },


      /**
       * @method MapZoon
       * @created September 4th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Returns the current zoom level of the map.
       * @returns Number
       */

      MapZoom() {

        if (this.instance) {
          return this.instance.getZoom() || this.zoom;
        }

      },


      /**
       * @method MapCenter
       * @created September 4th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Returns the latitude and longitude of the exact center of the map.
       * @returns Object
       */

      MapCenter() {

        if (this.instance) {
          const lat = this.MapBounds.getCenter().lat();
          const lng = this.MapBounds.getCenter().lng();

          return new google.maps.LatLng(lat, lng)
        }

      },


      /**
       * @method MapCompass
       * @created September 4th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Returns an object with the geographical coordinates of
       * each cardinal direction of the map (north, east, south, west).
       * @returns Object
       */

      MapCompass() {

        if (this.instance) {
          let north = new google.maps.LatLng(this.MapBounds.getNorthEast().lat(), this.MapCenter.lng());
          let south = new google.maps.LatLng(this.MapBounds.getSouthWest().lat(), this.MapCenter.lng());
          let east  = new google.maps.LatLng(this.MapCenter.lat(), this.MapBounds.getNorthEast().lng());
          let west  = new google.maps.LatLng(this.MapCenter.lat(), this.MapBounds.getSouthWest().lng());

          return { north, south, east, west }
        }

      },


      /**
       * @method MapState
       * @created September 4th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Returns the latitude and longitude of the exact center of the map.
       * @returns Object
       */

      MapState() {

        if (this.instance) {

          // Start with the default map settings.
          // These will be overwritten once the map has been
          // initiated and the map boundaries are available.

          let { lat, lng, zoom, radius, oldRadius } = this;

          if(this.MapBounds) {

            // The map has been initiated and the map
            // boundaries are available. Overwrite the
            // default map settings.

            lat = this.MapCenter.lat();
            lng = this.MapCenter.lng();
            zoom = this.instance.getZoom();

            // Make sure the the Google Maps geometry library
            // has been loaded in and is available before
            // attempting to use it for calculating the radius.

            if(google.maps.geometry) {

              // Reference the Google Maps geometry library.

              const sphere = google.maps.geometry.spherical;

              // Calculate the height of the map in pixels by getting
              // the distance between the north and south coordinates.

              const height = sphere.computeDistanceBetween(this.MapCompass.north, this.MapCompass.south);

              // Calculate the width of the map in pixels by getting
              // the distance between the east and west coordinates.

              const width = sphere.computeDistanceBetween(this.MapCompass.east, this.MapCompass.west);

              // Determine the direction we'll be measuring the radius from.
              // We want to measure from the side of the map that's smaller
              // in order to avoid the search radius extending beyond the
              // physical boundaries of the map on the screen.

              let boundary = height >= width ? this.MapCompass.east : this.MapCompass.north;

              // Calculate the radius by measuring the distance between the
              // absolute center of the map and the boundary. We then divide
              // the distance by the radian (1609.34) for the km/mi conversion.

              radius = oldRadius = (sphere.computeDistanceBetween(this.MapCenter, boundary) - this.radian) / this.radian;

            } else {

              // If the Google Maps geometry library isn't available
              // we can come up with a rough estimate for the search
              // radius by using metric radians.

              let x1 = this.MapCenter.lat() / 57.2958;
              let y1 = this.MapCenter.lng() / 57.2958;
              let x2 = this.MapCompass.north.lat() / 57.2958;
              let y2 = this.MapCompass.east.lng() / 57.2958;

              radius = oldRadius = this.radian * Math.acos(Math.sin(x1) * Math.sin(x2) + Math.cos(x1) * Math.cos(x2) * Math.cos(y2 - y1));

            }
          }

          // Restrict the search radius to a maximum value.
          // This ensures the radius is never too large and prevents
          // the API from being overloaded with results.

          radius = oldRadius = radius > this.options.maximumRadius ? this.options.maximumRadius : radius;

          return { lat, lng, zoom, radius, oldRadius }

        }

      }

    },

    created() {
      /**
       * @created October 28th, 2021
       * @author Drew Barrows <drew@nicindustries.com>
       * @description Sets the users' browser latitude and longitude when the map page loads.
       */

      const getPosition = (position) => {
        const latitude  = position.coords.latitude;
        const longitude = position.coords.longitude;

        this.lat = latitude;
        this.lng = longitude;
      };

      const positionError = (err) => {
        // Display an error message if the user has allowed location services but an error occurs.
        if (err.code !== GeolocationPositionError.PERMISSION_DENIED) {
          this.$parent.$refs.notifications.add({
            delay: false,
            type: 'error',
            icon: 'exclamation-triangle',
            title: 'Map Geolocation Error',
            message: err.message,
          });
        }

        // This will set Jackson County as the browser default if a location error exists
        this.lat = 42.3265;
        this.lng = -122.8756;

      };

      navigator.geolocation.getCurrentPosition(getPosition, positionError);
    },

    async mounted() {

      let params = new URLSearchParams(window.location.search);
      this.searchQuery = params.get("q");

      if (!this.hasSearchQuery) {
        setTimeout(() => {
          this.visible = true;
        }, 1000);
      }

      if(this.searchQuery) {

        await this.InitializeMap(this);

      }

      // Checks if a lat value exits in the URL. Then loads and updates the map based on those coordinates.
      if (params.has('lat')) {

        let coords = {
          lat: parseFloat(params.get('lat')),
          lng: parseFloat(params.get('lon')),
        };

        let mapOptions = {
          zoom: this.zoom,
          options: this.options,
          selector: this.selector,
        };

        this.InitializeMap({...coords, ...mapOptions});
        this.GetLocationName(coords)

      }

    /**
     * @method GetUsersGeoLocation
     * @modified July 27th, 2021
     * @author Drew Barrows <drew@nicindustries.com>
     * @description initializes the map/result data on page load without needing
     * a 'q' url param. #17864; requested it not load until prompted.
     */

      // else {

       // await this.GetUsersGeoLocation();

      // }

    },

    methods: {

      /**
       * @method GetUsersGeoLocation
       * @created February 1st, 2021
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Creates the Google Map instance and listens for events.
       * @returns void
       */

      async GetUsersGeoLocation() {
        this.$root.applicators = [];
        this.$root.loadingApplicators = true;

        if(!navigator.geolocation) {
          return this.InitializeMap(this);
        }

        navigator.geolocation.getCurrentPosition(
          async ({ coords }) => {
            this.searchQuery = await this.GetLocationName(this);
            this.zoom = 10;
            this.lat = coords.latitude;
            this.lng = coords.longitude;
            this.UpdateURLSearchParam();
            return this.InitializeMap(this);
          },

          async error => {
            return this.InitializeMap(this)
          });

      },

      /**
       * @method InitializeMap
       * @created September 4th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Creates the Google Map instance and listens for events.
       * @returns void
       */

      async InitializeMap({ lat, lng, zoom, options, selector }) {

        this.hasSearchQuery = true;
        this.$root.loadingApplicators = false;

        if(!this.instance) {

          let settings = { center: { lat, lng }, options, zoom };

          if(this.searchQuery) {

            let { lat, lng, zoom } = await this.GetGeoCoordinates();
            this.UpdateURLSearchParam();

            settings.center = { lat, lng };
            settings.zoom = zoom;

          }

          this.instance = new google.maps.Map(document.getElementById(selector), settings);
          this.instance.addListener("idle", async () => {

            if(!this.ready) {

              await this.MapUpdated();

              this.instance.addListener("dragend", async () => {
                if(options.updateOnDrag) await this.MapUpdated()
              });

              this.instance.addListener("zoom_changed", async () => {
                if(options.updateOnZoom) await this.MapUpdated()
              });

              this.ready = true;

            }

          });

        } else {

          await this.CenterMap({ lat, lng });
          await this.SetMapZoom(this.zoom);
          await this.MapUpdated();

        }

      },


      async GetLocationName({ lat, lng }) {

        return new Promise(async resolve => {

          let latLng = new google.maps.LatLng(lat, lng);
          await this.geocoder.geocode({ latLng }, async results => {
            this.searchQuery = resolve(results.filter(result => result.address_components.length === 3)[0].formatted_address);
          });

        })

        .catch(err => {
          console.log(err);
        });

      },

      async GetGeoCoordinates() {

        return new Promise(async resolve => {

          function setZoomLevel(address) {
            // Example: United States
            if (address.length === 1) return 6;

            // Example: Oregon
            if (address.length === 2) return 7;

            // Example: Jackson County
            if (address.length === 3) return 8;

            // Example: White City
            if (address.length >= 4) return 11;
          }

          await this.geocoder.geocode({ address: this.searchQuery }, async results => {

            if(results?.length) {

              let lat, lng, zoom;
              let location = results[0];
              let address = location.address_components;

              lat = location.geometry.location.lat();
              lng = location.geometry.location.lng();

              zoom = setZoomLevel(address);

              // Zoom out once on mobile devices.
              if (window.innerWidth <= 767) zoom -= 1;

              resolve({ lat, lng, zoom });

            } else {
              // If we have an error, default to medford county, and return error message to try again.
              let lat, lng, zoom;
              lat = 42.3265152;
              lng = -122.8755949;
              zoom = 11;

              this.$parent.$refs.notifications.add({
                delay: false,
                type: "error",
                icon: "exclamation-triangle",
                title: "Map Geolocation Error",
                message: "Could not find location. Please try again."
              });
              this.dismissNotification();
              resolve({ lat, lng, zoom });

            }

          })

        });

      },


      /**
       * @method LocationSearch
       * @created September 18th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Listens for when the map is idle.
       * @returns void
       */

      async LocationSearch() {

        if(!this.hasSearchQuery || !navigator.geolocation) {

          return this.InitializeMap(this);

        }

        this.UpdateURLSearchParam();

        const { lat, lng, zoom } = await this.GetGeoCoordinates();
        await this.SetMapLocation({ lat, lng, zoom });

      },


      UpdateURLSearchParam() {

        let params = new URLSearchParams(window.location.search);
        params.set("q", this.searchQuery);

        window.history.replaceState(null, null, `?${params.toString()}`);

      },


      async SetMapLocation({ lat, lng, zoom }) {

        await this.CenterMap({ lat, lng });
        await this.SetMapZoom(zoom);
        await this.MapUpdated();

      },


      /**
       * @method MapUpdated
       * @created September 4th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Listens for when the map is idle.
       * @returns void
       */

      async MapUpdated(filters = this.filters || {}) {

        this.filters = filters;

        if(this.filters.radius && this.filters.radius.length > 0){
            this.MapState.radius = this.filters.radius;
          }else{
            this.MapState.radius = this.MapState.oldRadius;
          }

        await this.RefreshMap();
        this.DrawMapRadius();

        this.$emit("updated", {
          state: this.MapState,
          filters: this.filters
        });

      },


      /**
       * @method MapMarkerClicked
       * @created September 18th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Listens for when a map marker is clicked.
       * @returns void
       */

      async MapMarkerClicked({ applicator, pin }) {

        if(this.options.centerOnSelectedMarker) {

          const lat = applicator.geo_coords.lat;
          const lng = applicator.geo_coords.lon;

          await this.CenterMap({ lat, lng });

        }

      },


      /**
       * @method DeselectMarkers
       * @created September 17th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Listens for when the map is idle.
       * @returns void
       */

      async DeselectMarkers() {

        await this.$refs.markers?.forEach(marker => marker.pin.div.removeAttribute("aria-selected"));

      },


      /**
       * @method CenterMap
       * @created September 4th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Centers the map on a lat/lng location.
       * @returns void
       */

      async CenterMap({ lat, lng }) {

        await this.instance?.panTo({ lat, lng });

      },



      /**
       * @method SetMapZoom
       * @param { number } level
       * @created September 14th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Adjusts the maps zoom level using incremental values.
       * @returns void
       */

      async SetMapZoom(level) {

        await this.instance.setZoom(level);

      },


      /**
       * @method DrawMapRadius
       * @created September 4th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Shows a visual representation of the map search radius.
       * @returns void
       */

      DrawMapRadius() {

        if(this.options.showRadius) {

          const { lat, lng } = this.MapState;
          this.radiusCircle = new google.maps.Circle({
            strokeColor: "#3E6B85",
            strokeOpacity: 0.25,
            strokeWeight: 2,
            fillColor: "#3E6B85",
            fillOpacity: 0.125,
            map: this.instance,
            center: { lat, lng },
            radius: this.MapState.radius * this.radian,
            clickable: false
          });

        }

      },


      /**
       * @method RemoveMapRadius
       * @created September 18th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Shows a visual representation of the map search radius.
       * @returns void
       */

      RemoveMapRadius() {

        if(this.radiusCircle) {
          this.radiusCircle.setMap(null);
          this.radiusCircle = undefined;
        }

      },


      /**
       * @method ClearMarkers
       * @created September 4th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Removes all current markers from the map.
       * @returns void
       */

      async ClearMarkers() {

        await this.$refs.markers?.forEach(marker => marker.clear());

      },


      /**
       * @method RefreshMap
       * @created September 15th, 2020
       * @author Sebastian Inman <sinman@nicindustries.com>
       * @description Refreshes the map state by removing all map
       * markers and resetting the radius circle.
       * @returns void
       */

      async RefreshMap() {

        await this.ClearMarkers();
        this.RemoveMapRadius();

      },

      dismissNotification() {

        setTimeout(() => {
          this.$parent.$refs.notifications.notifications = [];
        }, 1000);

      }
    }
  }
</script>

<style lang="scss">
  $imgDir: '../../../img/';

  .slide-enter-active,
  .slide-leave-active {
    transition: transform 250ms cubic-bezier(0.5, 0, 0.15, 0.8);
  }

  .slide-enter,
  .slide-leave-to {
    transform: translate3d(-100%, 0, 0);
  }

  #applicator-map.applicator-map-overlay {
    min-height:auto;
  }

  #applicator-map {
    --applicator-border-radius: 4px;
    --applicator-filters-width: 280px;
    --sidebar-padding: 32px;
    --sidebar-width: 750px;

    flex-direction: row;
    flex-wrap: nowrap;
    display: flex;
    z-index: 11;
    width: 100%;

    min-height: 840px;

    @media screen and (max-width: 1024px) {
      flex-direction: column;
    }

    &-controls {
      right: var(--sidebar-padding);
      left: var(--sidebar-padding);
      top: var(--sidebar-padding);
      align-items: flex-start;
      pointer-events: none;
      flex-direction: row;
      position: absolute;
      z-index: 2;
      display: flex;

      &-left,
      &-right {
        flex-direction: row;
        display: flex;
        flex: 1;
      }

      &-left {
        justify-content: flex-start;

        .map-action + .map-action {
          margin-left: 16px;
        }
      }

      &-right {
        justify-content: flex-end;

        .map-action + .map-action {
          margin-right: 16px;
        }
      }
    }

    .map-action {
      box-shadow: 0 1px 2px 0 rgba(black, 0.15);
      transition: box-shadow 200ms ease-in-out;
      background-color: white;
      pointer-events: all;
      position: relative;
      border-radius: 4px;
      padding: 8px;
      z-index: 2;

      &:hover,
      &:focus-within {
        box-shadow: 0 10px 20px rgba(black, 0.19), 0 6px 6px rgba(black, 0.23);
      }

      @media screen and (max-width: 1024px) {
        border-radius: 0;
      }
    }

    &-filter,
    &-search,
    &-zoom,
    &-view,
    &-sidebar,
    &-list {
      display: flex;
    }

    &-sidebar,
    &-search,
    &-list,
    &-zoom,
    &-view {
      flex-direction: column;
    }

    &-zoom {
      z-index: 2;
      padding: 0;

      @media screen and (max-width: 1024px) {
        display: none;
      }

      &-in,
      &-out {
        justify-content: center;
        align-items: center;
        background: none;
        display: flex;
        height: 40px;
        width: 40px;
        border: 0;
      }
    }

    &-view {
      position: relative;
      outline: none;
      flex: 1;

      @media screen and (max-width: 1024px) {
        pointer-events: none;
        visibility: hidden;
        position: absolute;
        // height: 100vh;
        width: 100vw;
      }

      &-init {
        outline: none;
        width: 100%;
        flex: 1;
      }
    }

    &-markers {
      visibility: hidden;
      display: none;
    }

    &.sidebar-left {
      #applicator-map-sidebar.landing {
        border-right: none;
      }

      #applicator-map-sidebar {
        border-right: 1px solid #dbdbdb;
        order: 1;

        &-results {
          padding-left: var(--sidebar-padding);

          &-info {
            margin: 10px 0;
          }
        }
      }

      #applicator-map-content {
        order: 2;
      }
    }

    &.sidebar-right {
      #applicator-map-sidebar {
        border-left: 1px solid #dbdbdb;
        order: 2;

        &-results {
          padding-right: var(--sidebar-padding);
        }

        @media screen and (max-width: 1024px) {
          order: 1;
        }
      }

      #applicator-map-content {
        order: 1;

        @media screen and (max-width: 1024px) {
          order: 2;
        }
      }
    }

    &-sidebar.landing {
      z-index: 0;
      max-width:50%;
      width: 50%;

      .bg-image{
        filter: blur(4px);
        -webkit-filter: blur(4px);
        height: 100%;
        width: 100%;
        background-repeat: no-repeat;
        background-size: cover;
        background-image: url($imgDir + 'applicator-map-placeholder.jpg');
      }

      border-right:none;

      @media screen and (max-width: 1024px) {
        border: none;
        width: 100%;
        max-width:100%;
      }
    }

    &-sidebar {
      background-color: #f9fafc;
      flex-direction: row;
      position: relative;
      max-width: 100%;
      display: flex;
      z-index: 2;

      @media screen and (max-width: 1024px) {
        border: none;
        width: 100%;
      }

      &-filters,
      &-results {
        padding: var(--sidebar-padding);
        flex-direction: column;
        position: relative;
        display: flex;
      }

      &-filters.landing {
        width:0px;
      }

      &-filters {
        max-height: calc(100vh - var(--desktop-header-size));
        width: var(--applicator-filters-width);
        border-right: 1px solid #dbdbdb;
        top: var(--desktop-header-size);
        overflow-y: auto;
        position: sticky;
        z-index: 2;

        @media screen and (max-width: 1500px) {
          transition: transform 200ms ease-in-out;
          transform: translate3d(-100%, 0, 0);
          top: var(--desktop-header-size);
          background-color: #f9fafc;
          max-height: 100vh;
          overflow-y: auto;
          position: fixed;
          width: auto;
          bottom: 0;
          left: 0;

          &[visible-on-desktop] {
            transform: translate3d(0, 0, 0);
            box-shadow: 0 10px 20px rgba(black, 0.19), 0 6px 6px rgba(black, 0.23);
          }
        }

        @media screen and (max-width: 1024px) {
          transform: translate3d(0, 0, 0);
          padding: var(--sidebar-padding);
          top: var(--mobile-header-size);
          box-shadow: none;
          display: none;
          border: none;
          right: 0;

          &[visible-on-mobile] {
            display: flex;
          }
        }
      }

      &-results.landing {
        width: 100%;

        @media screen and (max-width: 1024px) {
          filter: none !important;
        }
      }

      &-results {
        width: var(--sidebar-width);
        align-items: flex-start;
        z-index: 1;

        @media screen and (max-width: 1024px) {
          width: 100%;
        }

        > .btn {
          margin-top: 16px;
        }
      }

      h2 {
        font-family: 'Teko', sans-serif;
        text-transform: uppercase;
        letter-spacing: 1.5px;
        line-height: 0.75;
        font-weight: 500;
        font-size: 4rem;
        margin: 0;
        text-align: center;

        + strong {
          margin-top: 12px;
        }
      }

      &-loading {
        background-color: #efefef;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        border-radius: 4px;
        margin-top: 24px;
        display: flex;
        padding: 16px;
        width: 100%;

        > strong {
          margin-top: 16px;
        }
      }
    }

    &-list {
      list-style: none;
      margin-top: 24px;
      width: 100%;
    }

    &-content {
      height: calc(100vh - calc(var(--desktop-header-size) + 1px));
      top: calc(var(--desktop-header-size) + 1px);
      flex-direction: column;
      position: sticky;
      display: flex;
      flex: 1;

      @media screen and (max-width: 1024px) {
        height: auto;
        z-index: 2;
        bottom: 0;
        pointer-events: none;

        &.applicator-selected {
          display: none;
        }
      }
    }

    &-no-results,
    &-end-of-results {
      width: 100%;
      border-radius: 4px;
      font-weight: 600;
      padding: 16px;
      display: flex;
    }

    &-no-results {
      border: 1px solid #ffbb35;
      background-color: #f7f2d2;
    }

    &-end-of-results {
      border: 1px solid #40cbf9;
      background-color: #d7f4fe;
    }

    &[mobile-filters-open] {
      @media screen and (max-width: 1024px) {
        > #applicator-map-sidebar {
          background-color: red;
          z-index: 3;
        }
      }
    }
  }

  @media screen and (max-width: 1024px) {
    #applicator-map {
      height: 80vh;
    }

    .applicator-map-overlay {
      #applicator-map-view .map-placeholder {
        display: none;
      }

      #applicator-map-list .result-placeholder {
        display: none;
      }

      #applicator-map-content {
        pointer-events: all !important;
        min-height: auto;
        background: #fff;
      }

      #applicator-map-search {
        background-color: transparent;
        box-shadow: none;
        border-top: none;

        .mobile-toggle {
          display: none;
        }
      }
    }
  }
</style>
